import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Pizza, UtensilsCrossed, Salad, Beef, Flame, Fish, Clock,
  Sparkles, Soup, Cake, Croissant, Droplet, Plus, GlassWater,
  Beer, Coffee, Wine, FlaskRound, Baby, ChevronLeft, ChevronRight
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useScroll } from '../context/ScrollContext';
import { categories } from '../data/menuData';

const categoryIcons = {
  pizza: Pizza, pasta: UtensilsCrossed, salads: Salad, burgers: Beef,
  serbian_grill: Flame, main_dishes: UtensilsCrossed, fish: Fish,
  fish_grill: Fish, alaminute: Clock, baked_potatoes: Flame,
  risotto: Soup, specials: Sparkles, soups: Soup, desserts: Cake,
  breads: Croissant, sides: UtensilsCrossed, sauces: Droplet,
  extras: Plus, soft_drinks: GlassWater, hot_drinks: Coffee,
  spirits: FlaskRound, beer: Beer, appetizers: Flame, wines: Wine,
  kids_menu: Baby
};

const drinkCategoryIds = ['soft_drinks', 'hot_drinks', 'spirits', 'beer', 'wines'];
const foodCategories = categories.filter((category) =>
  !drinkCategoryIds.includes(category.id) && category.id !== 'kids_menu'
);
const drinkCategories = categories.filter((category) => drinkCategoryIds.includes(category.id));

const saveActiveCategory = (categoryId) => {
  sessionStorage.setItem('activeCategory', categoryId);
};

const getSavedCategory = () => sessionStorage.getItem('activeCategory') || 'pizza';

const ScrollIndicator = ({ position, isDark }) => {
  const prefersReducedMotion = useReducedMotion();

  if (position === 'none') return null;

  const isAtEnd = position === 'end';
  const Icon = isAtEnd ? ChevronLeft : ChevronRight;

  return (
    <div
      aria-hidden="true"
      className={`absolute top-0 bottom-0 z-10 w-14 pointer-events-none category-desktop:hidden flex items-center backdrop-blur-[1px] ${
        isAtEnd ? '-left-3 sm:-left-4 justify-start pl-1' : '-right-3 sm:-right-4 justify-end pr-1'
      } ${
        isDark
          ? isAtEnd
            ? 'bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/95 to-transparent'
            : 'bg-gradient-to-l from-[#0A0A0A] via-[#0A0A0A]/95 to-transparent'
          : isAtEnd
            ? 'bg-gradient-to-r from-white via-white/95 to-transparent'
            : 'bg-gradient-to-l from-white via-white/95 to-transparent'
      }`}
    >
      <motion.span
        className={`${isAtEnd ? 'ml-0.5' : 'mr-0.5'} ${isDark ? 'text-white/70' : 'text-gray-500'}`}
        animate={{ x: prefersReducedMotion ? 0 : isAtEnd ? [3, -1, 3] : [-3, 1, -3] }}
        transition={prefersReducedMotion ? { duration: 0 } : { repeat: Infinity, duration: 1.4 }}
      >
        <Icon size={21} strokeWidth={2.25} />
      </motion.span>
    </div>
  );
};

const CategoryScroller = ({ children, activeCategory, isDark, testId }) => {
  const scrollerRef = useRef(null);
  const [indicatorPosition, setIndicatorPosition] = useState('none');

  const updateIndicator = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
    if (maxScrollLeft <= 2) {
      setIndicatorPosition('none');
      return;
    }

    setIndicatorPosition(maxScrollLeft - scroller.scrollLeft <= 2 ? 'end' : 'right');
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;

    updateIndicator();
    scroller.addEventListener('scroll', updateIndicator, { passive: true });
    window.addEventListener('resize', updateIndicator);

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateIndicator)
      : null;
    resizeObserver?.observe(scroller);

    return () => {
      scroller.removeEventListener('scroll', updateIndicator);
      window.removeEventListener('resize', updateIndicator);
      resizeObserver?.disconnect();
    };
  }, [updateIndicator]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    const activeTab = scroller?.querySelector('[data-active="true"]');
    if (!scroller || !activeTab) return;

    const scrollPosition = activeTab.offsetLeft
      - (scroller.clientWidth / 2)
      + (activeTab.offsetWidth / 2);
    scroller.scrollTo({ left: scrollPosition, behavior: 'smooth' });
  }, [activeCategory]);

  return (
    <div className="relative">
      <div
        ref={scrollerRef}
        data-testid={testId}
        className="flex category-desktop:flex-wrap category-desktop:justify-center gap-2.5 overflow-x-auto category-desktop:overflow-visible scrollbar-hide pr-8 category-desktop:pr-0"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {children}
      </div>
      <ScrollIndicator position={indicatorPosition} isDark={isDark} />
    </div>
  );
};

const CategoryTabs = ({ activeCategory, onCategoryChange }) => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const { categoryOffset } = useScroll();

  useEffect(() => {
    const savedCategory = getSavedCategory();
    if (savedCategory && savedCategory !== activeCategory) {
      onCategoryChange(savedCategory);
    }
  }, [activeCategory, onCategoryChange]);

  const handleCategoryClick = (categoryId) => {
    saveActiveCategory(categoryId);
    onCategoryChange(categoryId);
  };

  const renderCategoryButton = (category) => {
    const isActive = activeCategory === category.id;
    const IconComponent = categoryIcons[category.id];

    return (
      <motion.button
        key={category.id}
        data-active={isActive}
        data-testid={`category-tab-${category.id}`}
        onClick={() => handleCategoryClick(category.id)}
        className={`
          flex items-center gap-2.5 px-5 py-3 category-desktop:px-4 category-desktop:py-2 rounded-full
          border text-base category-desktop:text-sm font-medium transition-all duration-300 flex-shrink-0
          ${isActive
            ? 'bg-[#008000] text-white border-[#008000] shadow-[0_0_15px_rgba(0,128,0,0.3)]'
            : isDark
              ? 'bg-white/5 text-white/80 border-white/10 hover:bg-white/10 hover:text-white'
              : 'bg-black/5 text-gray-600 border-black/10 hover:bg-black/10 hover:text-gray-900'
          }
        `}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {IconComponent && <IconComponent size={18} className="flex-shrink-0 category-desktop:w-4 category-desktop:h-4" />}
        <span className="whitespace-nowrap">{language === 'bg' ? category.name_bg : category.name_en}</span>
      </motion.button>
    );
  };

  return (
    <div
      data-category-tabs
      className={`sticky z-40 backdrop-blur-xl border-b py-3 sm:py-4 transition-colors duration-300 ${
        isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-black/10'
      }`}
      style={{
        top: `${categoryOffset}px`,
        transition: 'top 0.2s cubic-bezier(0.2, 0.9, 0.4, 1.1)'
      }}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-4">
        <CategoryScroller activeCategory={activeCategory} isDark={isDark} testId="category-tabs">
          {foodCategories.map(renderCategoryButton)}
        </CategoryScroller>

        <div className={`my-3 border-t ${isDark ? 'border-white/10' : 'border-black/10'}`} />

        <CategoryScroller activeCategory={activeCategory} isDark={isDark} testId="drink-category-tabs">
          <span className={`hidden category-desktop:flex items-center text-xs font-medium mr-2 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
            {language === 'bg' ? 'Напитки:' : 'Drinks:'}
          </span>
          {drinkCategories.map(renderCategoryButton)}
        </CategoryScroller>
      </div>
    </div>
  );
};

export default CategoryTabs;
